import { type ReactNode, useId } from 'react';

import { Select as SelectMenu, type SelectOption } from './select-menu';

export interface SelectFieldProps {
  label?: string;
  error?: string;
  helperText?: string;
  placeholder?: string;
  /** Either pass `options` or `<option>` children - both are supported. */
  options?: SelectOption[];
  children?: ReactNode;
  value?: string;
  defaultValue?: string;
  onChange?: (e: { target: { value: string; name?: string } }) => void;
  onBlur?: () => void;
  name?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

/**
 * A labelled dropdown field with the rounded, self-drawn option list.
 *
 * The same visual contract as the native form `Select` (label above, helper text
 * or error below) but built on the Radix menu, because a native `<select>`'s
 * open list is painted by the OS and cannot be rounded or themed.
 *
 * INSIDE A FORM, drive it with react-hook-form's `Controller`, not `register()`:
 * the control is a button, so there is no `.value` for an uncontrolled ref to
 * read. `onChange` already receives a native-looking `{ target: { value, name } }`,
 * so `field.onChange(e.target.value)` is the whole wiring.
 */
const SelectField = ({
  label,
  error,
  helperText,
  placeholder,
  options,
  children,
  value,
  defaultValue,
  onChange,
  onBlur,
  name,
  disabled,
  className,
  id,
}: SelectFieldProps) => {
  const generatedId = useId();
  const fieldId = id ?? generatedId;

  return (
    <div className="flex flex-col gap-1" onBlur={onBlur}>
      {/*
        A <span>, not a <label>: the control this names is a button, and htmlFor
        cannot target one. The trigger gets the same text as its aria-label so
        screen readers still announce it.
      */}
      {label && (
        <span id={`${fieldId}-label`} className="text-sm font-medium text-ink">
          {label}
        </span>
      )}
      <SelectMenu
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        name={name}
        disabled={disabled}
        placeholder={placeholder}
        error={!!error}
        className={className}
        aria-label={label}
        options={options}
      >
        {children}
      </SelectMenu>
      {error && (
        <p id={`${fieldId}-error`} className="text-xs text-danger" role="alert">
          {error}
        </p>
      )}
      {helperText && !error && <p className="text-xs text-ink/60">{helperText}</p>}
    </div>
  );
};

export default SelectField;
